import { AlertType, SearchQueryOperator, ExecuteSimpleSearchQueryInput } from 'Generated/schema';
import { v4 as uuidv4 } from 'uuid';
import { SavedQueryDetails } from 'Source/graphql/fragments/SavedQueryDetails.generated';
import {
  DEFAULT_LOGS_DB_NAME_SNOWFLAKE,
  DEFAULT_RULE_ERRORS_DB_NAME_SNOWFLAKE,
  DEFAULT_RULE_MATCHES_DB_NAME_SNOWFLAKE,
} from 'Source/constants';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import { OPERATORS } from '../constants';
import {
  ColumnProperty,
  Filter,
  Filters,
  Metadata,
  MinimalQueryInfo,
  OrsFilter,
  QueryBuilderUrlParams,
  QueryFilter,
  SimpleSearchQueryFilter,
} from '../types';
import { getInputGroup } from './filterOperatorInputHelpers';
import { PantherColumnsAliases } from '../CrossLogSearch/helpers/aliases';

import { FilterKeyType } from '../../types';

export function doesActiveQueryMatchSavedQuery(
  queryCookie: string,
  newQuery: ExecuteSimpleSearchQueryInput,
) {
  if (!queryCookie) {
    return false;
  }

  const parsedCookie = JSON.parse(queryCookie);
  const oldQuery = { params: { ...(parsedCookie?.params ?? {}), sortDir: 'desc' } };
  // Sort tables and databases to ensure that the order of the tables and databases
  // in the query cookie does not affect the comparison
  oldQuery.params.tables = oldQuery.params.tables ? sortBy(oldQuery.params.tables, t => t) : [];
  oldQuery.params.databases = oldQuery.params.databases
    ? sortBy(oldQuery.params.databases, db => db)
    : [];

  const newQueryCopy = {
    ...newQuery,
    params: { ...(newQuery.params ?? {}), sortDir: 'desc' },
  };
  newQueryCopy.params.tables = newQueryCopy.params.tables
    ? sortBy(newQueryCopy.params.tables, t => t)
    : [];
  newQueryCopy.params.databases = newQueryCopy.params.databases
    ? sortBy(newQueryCopy.params.databases, db => db)
    : [];

  // The absolute timestamps will become "current" if a relative time saved query is run
  // So, ignore the absolute timestamps while checking whether a relative time saved query is dirty.
  // We delete the start and end times if the relative time is the same in both queries.
  if (oldQuery.params.relativeTime || newQueryCopy.params.relativeTime) {
    if (oldQuery.params.relativeTime === newQueryCopy.params.relativeTime) {
      delete oldQuery.params.startTime;
      delete oldQuery.params.endTime;
      delete newQueryCopy.params.startTime;
      delete newQueryCopy.params.endTime;
    }
  }
  // If neither query has a relative time, we can delete the relative time from both queries to ensure
  // that the comparison is accurate. This removes the field from both objects since it's included
  // by default in the newQueryCopy
  else if (!(oldQuery.params.relativeTime && newQueryCopy.params.relativeTime)) {
    delete oldQuery.params.relativeTime;
    delete newQueryCopy.params.relativeTime;
  }

  return isEqual(oldQuery, newQueryCopy);
}

/**
 * We need to return an object that looks like:
 * {
 *    ors: [
 *      {
 *        ands: []
 *      }
 *    ]
 * }
 *
 * The incoming object will look different depending on how many filters are in place.
 * It could just contain a filter object, an object with an ands key where the value is
 * an array of filter objects, or an object with an ors key where the value is an array
 * of objects with an ands key where the value is an array of filter objects. In all cases
 * we want to return an object of a consistent shape explained above.
 */
export const parseQueryFilter = (queryFilter: SimpleSearchQueryFilter): OrsFilter | null => {
  if (queryFilter) {
    const parsedQuery = {
      ors: [],
    } as OrsFilter;
    if ('ands' in queryFilter) {
      parsedQuery.ors.push({
        ands: queryFilter.ands,
      });
    } else if ('ors' in queryFilter) {
      parsedQuery.ors = queryFilter.ors.map(orGroup => {
        if ('ands' in orGroup) {
          return orGroup;
        }
        return {
          ands: [orGroup],
        };
      });
    } else {
      parsedQuery.ors.push({
        ands: [queryFilter],
      });
    }

    return parsedQuery;
  }
  return null;
};

const isISOString = (str: string) => {
  const isoRegExp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
  return isoRegExp.test(str);
};

const findMatchingOperator = (queryFilter: QueryFilter) => {
  const matchingOperators = OPERATORS.filter(o => o.value === queryFilter.operator);
  // if there is only one operator with the same value, we can return early
  if (matchingOperators.length === 1) {
    return matchingOperators[0];
  }

  // Boolean equals operator has the same "equals" value both for "Is True" and "Is False"
  // so we need to filter by initialFieldValue too
  if (
    queryFilter.operator === SearchQueryOperator.Equals &&
    typeof queryFilter.value === 'boolean'
  ) {
    return matchingOperators.find(o => o.initialFieldValue === queryFilter.value);
  }

  // There might be multiple operators with the same value,
  // so we need to filter by type too. e.g. there might be
  // an operator named "equals" in both the "string" and "number" groups.
  // Thankfully, this can only happen for 'time' 'number' and 'string' types
  const value = Array.isArray(queryFilter.value) ? queryFilter.value[0] : queryFilter.value;
  let type = 'string';
  if (typeof value === 'number') {
    type = 'number';
  } else if (isISOString(value)) {
    type = 'time';
  }

  return matchingOperators.find(o => o.group === type) || matchingOperators[0];
};

export const createFilterObjectFromQueryFilter = (
  queryFilter: QueryFilter,
  properties: ColumnProperty[],
) => {
  const operator = findMatchingOperator(queryFilter);
  // There might be multiple columns with the same name,
  // so we need to find the one that matches the type too,
  // e.g there might be a column named "source_id" in both
  // the "logs" and "rule_matches" tables, but the type of
  // the "logs" table is "string" and the type of the "rule_matches"
  // table is "number"
  const matchingColumns = properties.filter(column => column.path === queryFilter.key);
  let matchingColumn = matchingColumns[0];
  if (matchingColumns.length > 1) {
    matchingColumn =
      matchingColumns.find(c => getInputGroup(c.type) === operator.group) || matchingColumn;
  }
  if (matchingColumn) {
    // Adding alias to column name if it exists
    const col = {
      ...matchingColumn,
      alias: PantherColumnsAliases[matchingColumn.path],
    };
    return {
      key: col,
      keyType: FilterKeyType.Object,
      operator,
      value: queryFilter.value,
      uuid: uuidv4(),
    };
  }

  // Since there isn't a matching column, we assume this is a nested field string
  return {
    key: queryFilter.key,
    keyType: FilterKeyType.String,
    operator,
    value: queryFilter.value,
    uuid: uuidv4(),
  };
};

export const transformQueryFilters = (
  queryFilters: OrsFilter,
  properties: ColumnProperty[],
): Filters => {
  const filters = {
    ors: queryFilters.ors.reduce((filterGroups, { ands }) => {
      const filtersArray = ands.map(queryFilter => {
        return createFilterObjectFromQueryFilter(queryFilter, properties);
      }) as Filter[];

      filterGroups.push({
        uuid: uuidv4(),
        ands: filtersArray,
      });
      return filterGroups;
    }, []),
  };

  return filters as Filters;
};

export const createMetadataFromQueryCookie = (queryCookie: string): Metadata => {
  const parsedQueryCookie = JSON.parse(queryCookie);
  const metadata = {
    ...parsedQueryCookie?.params,
  };

  if (metadata.filters) {
    if (metadata.language === 'simplesearch') {
      metadata.simpleSearchQueryFilter = JSON.parse(metadata.filters);
    }
    if (metadata.language === 'pantherflow') {
      metadata.query = metadata.filters;
    }
  }

  return metadata;
};

export const transformSavedQueryToMinimalQuery = (
  queryData: SavedQueryDetails,
): MinimalQueryInfo & { metadata: Metadata } => {
  const metadata = createMetadataFromQueryCookie((queryData as SavedQueryDetails)?.queryCookie);

  return {
    id: queryData.id,
    sql: queryData.sqlQuery,
    kind: queryData.queryKind,
    metadata,
  };
};

enum ENTITY_TO_COLUMN_MAP {
  sourceId = 'p_source_id',
  alertId = 'p_alert_id',
}

export const transformParamsToQueryFilters = ({
  sourceId,
  alertId,
}: Pick<QueryBuilderUrlParams, 'sourceId' | 'alertId'>): SimpleSearchQueryFilter => {
  const queryFilters: QueryFilter[] = [];
  if (sourceId) {
    queryFilters.push({
      key: ENTITY_TO_COLUMN_MAP.sourceId,
      operator: SearchQueryOperator.Equals,
      value: sourceId,
    });
  }

  if (alertId) {
    queryFilters.push({
      key: ENTITY_TO_COLUMN_MAP.alertId,
      operator: SearchQueryOperator.Equals,
      value: alertId,
    });
  }

  return { ands: queryFilters };
};

export const selectDatabaseFromQueryFilters = ({
  alertType,
}: Pick<QueryBuilderUrlParams, 'alertType'>): string => {
  const isAlertFromError = [AlertType.RuleError, AlertType.ScheduledRuleError].includes(alertType);
  if (isAlertFromError) {
    return DEFAULT_RULE_ERRORS_DB_NAME_SNOWFLAKE;
  }

  const isAlertMatch = [AlertType.Rule, AlertType.ScheduledRule].includes(alertType);
  if (isAlertMatch) {
    return DEFAULT_RULE_MATCHES_DB_NAME_SNOWFLAKE;
  }

  return DEFAULT_LOGS_DB_NAME_SNOWFLAKE;
};
